import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Copy, Video, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { AnimatedReel } from "../components/reels/AnimatedReel";
import { IG_REELS, getIgReel, reelDuration } from "../lib/igReels";
import { useAuth } from "../context/AuthContext";
import { useSeo } from "../lib/useSeo";

export default function InstagramReelStudio() {
  useSeo({ title: "Reel studio — CampCompass", noindex: true });
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const reel = getIgReel(slug) || IG_REELS[0];
  const pos = IG_REELS.indexOf(reel);
  const [mode, setMode] = useState("preview");
  const [count, setCount] = useState(3);

  useEffect(() => {
    if (mode !== "countdown") return;
    if (count === 0) { setMode("recording"); return; }
    const t = setTimeout(() => setCount((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [mode, count]);

  const onEnd = useCallback(() => { if (mode === "recording") setMode("done"); }, [mode]);
  const startRecording = () => { setCount(3); setMode("countdown"); };
  const go = (d) => { setMode("preview"); navigate(`/admin/instagram-reels/${IG_REELS[(pos + d + IG_REELS.length) % IG_REELS.length].slug}`); };
  const copy = () => navigator.clipboard.writeText(reel.caption).then(() => toast.success("Caption copied"));

  if (loading) return <div className="min-h-screen bg-[#0e0e0e]" />;
  if (!user || user.role !== "admin") {
    return <div className="min-h-screen flex items-center justify-center text-sm" data-testid="reel-studio-denied">Admins only. <Link to="/login" className="ml-2 underline">Log in</Link></div>;
  }

  const clean = mode === "countdown" || mode === "recording";
  return (
    <div className="min-h-screen bg-[#0e0e0e] text-white flex flex-col items-center justify-center" data-testid="reel-studio">
      {!clean && (
        <div className="w-full max-w-[560px] px-4 pt-4 pb-3 flex flex-wrap items-center justify-between gap-2">
          <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-white" data-testid="reel-studio-back"><ArrowLeft className="w-3.5 h-3.5" /> Admin</Link>
          <div className="flex items-center gap-1">
            <button onClick={() => go(-1)} className="p-2 hover:bg-white/10" data-testid="reel-studio-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs font-bold" data-testid="reel-studio-title">{pos + 1}/{IG_REELS.length} · {reel.title}</span>
            <button onClick={() => go(1)} className="p-2 hover:bg-white/10" data-testid="reel-studio-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-2">
            <button onClick={copy} className="inline-flex items-center gap-1.5 border border-white/30 px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-white/10" data-testid="reel-studio-copy"><Copy className="w-3.5 h-3.5" /> Caption</button>
            <button onClick={startRecording} className="inline-flex items-center gap-1.5 bg-[#ff4500] px-3 py-2 text-[11px] font-bold uppercase tracking-wider hover:bg-[#e63e00]" data-testid="reel-studio-record"><Video className="w-3.5 h-3.5" /> Record mode</button>
          </div>
        </div>
      )}
      <div className="relative" style={{ height: clean ? "100vh" : "calc(100vh - 140px)", aspectRatio: "9 / 16", maxWidth: "100vw" }}>
        <AnimatedReel
          key={`${reel.slug}-${mode === "recording" ? "rec" : "pre"}`}
          reel={reel}
          playing={mode === "preview" || mode === "recording"}
          loop={mode === "preview"}
          onEnd={onEnd}
          className="h-full w-full"
          testId="reel-studio-player"
        />
        {mode === "countdown" && (
          <div className="absolute inset-0 bg-black flex flex-col items-center justify-center" data-testid="reel-studio-countdown">
            <div className="font-display text-[120px] leading-none">{count}</div>
            <div className="text-xs text-white/60 mt-4">Start your screen recording now</div>
          </div>
        )}
        {mode === "done" && (
          <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center gap-3" data-testid="reel-studio-done">
            <div className="font-display text-3xl">That's a wrap</div>
            <p className="text-xs text-white/70 max-w-[240px] text-center">Stop recording, trim the first second, then upload with the copied caption.</p>
            <button onClick={() => setMode("preview")} className="inline-flex items-center gap-1.5 bg-white text-[#0e0e0e] px-4 py-2 text-xs font-bold uppercase" data-testid="reel-studio-replay"><RotateCcw className="w-3.5 h-3.5" /> Back to preview</button>
          </div>
        )}
      </div>
      {!clean && <p className="text-[10px] text-white/50 py-2" data-testid="reel-studio-duration">{Math.round(reelDuration(reel.scenes()) / 1000)}s · {reel.angle}</p>}
    </div>
  );
}
